import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import Parse from 'parse';

const SetOperatorRtpDialog = ({ open, onClose, onSave, config, mode, operators }) => {
  const [formData, setFormData] = useState({
    operatorId: '',
    gameSlug: '',
    gameModeId: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Dropdowns data
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [rtpLevels, setRtpLevels] = useState([]);
  const [rtpLevelsLoading, setRtpLevelsLoading] = useState(false);

  // Fetch games on dialog open
  useEffect(() => {
    if (!open) return;
    const fetchGames = async () => {
      setGamesLoading(true);
      try {
        const result = await Parse.Cloud.run('rgsListGamesForDropdown');
        if (result.success) {
          setGames(result.games);
        }
      } catch (error) {
        console.error('Error fetching games:', error);
      } finally {
        setGamesLoading(false);
      }
    };
    fetchGames();
  }, [open]);

  // Fetch RTP levels for a given game slug
  const fetchRtpLevelsForGame = async (gameSlug) => {
    if (!gameSlug) {
      setRtpLevels([]);
      return;
    }
    setRtpLevelsLoading(true);
    try {
      const result = await Parse.Cloud.run('rgsListGameRtpLevels', { gameSlug });
      if (result.success) {
        setRtpLevels(result.rtpLevels || []);
      }
    } catch (error) {
      console.error('Error fetching RTP levels:', error);
      setRtpLevels([]);
    } finally {
      setRtpLevelsLoading(false);
    }
  };

  // Populate form when dialog opens or config changes
  useEffect(() => {
    if (!open) return;

    setErrors({});

    if (mode === 'edit' && config) {
      setFormData({
        operatorId: config.operatorId || '',
        gameSlug: config.gameSlug || '',
        gameModeId: config.gameModeId || ''
      });
      // Always fetch RTP levels when opening in edit mode
      fetchRtpLevelsForGame(config.gameSlug);
    } else {
      setFormData({ operatorId: '', gameSlug: '', gameModeId: '' });
      setRtpLevels([]);
    }
  }, [mode, config, open]);

  // Fetch available game modes when user changes the game dropdown (create mode)
  const handleGameChange = (gameSlug) => {
    setFormData((prev) => ({ ...prev, gameSlug, gameModeId: '' }));
    if (errors.gameSlug) setErrors((prev) => ({ ...prev, gameSlug: '' }));
    fetchRtpLevelsForGame(gameSlug);
  };

  // Build flat list of all game modes for the dropdown
  const allGameModes = rtpLevels.flatMap((level) =>
    level.modes.map((m) => ({
      ...m,
      rtpLevel: level.rtpLevel
    }))
  );

  const validateForm = () => {
    const newErrors = {};
    if (!formData.operatorId) newErrors.operatorId = 'Operator is required';
    if (!formData.gameSlug) newErrors.gameSlug = 'Game is required';
    if (!formData.gameModeId) newErrors.gameModeId = 'Game Mode is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await Parse.Cloud.run('rgsSetOperatorRtp', {
        operatorId: formData.operatorId,
        gameSlug: formData.gameSlug,
        gameModeId: formData.gameModeId
      });
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving config:', error);
      setErrors({ submit: error.message || 'Failed to save operator game config' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  const selectSx = {
    backgroundColor: '#2B2B2B',
    color: '#fff',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
    '& .MuiSvgIcon-root': { color: '#fff' }
  };

  // Find the selected mode details for the info box
  const selectedMode = allGameModes.find((m) => m.gameModeId === formData.gameModeId);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#242424', m: 0 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          backgroundColor: '#242424',
          color: '#FFC107',
          pb: 2
        }}
      >
        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
          {mode === 'create' ? 'Assign Game Mode to Operator' : 'Edit Operator Game Mode'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '#242424', pt: 2, pb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
              {errors.submit}
            </Alert>
          )}

          {/* Operator Selection */}
          <Box>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
              Operator *
            </Typography>
            <FormControl fullWidth size="small" error={!!errors.operatorId}>
              <Select
                value={formData.operatorId}
                onChange={(e) => {
                  setFormData({ ...formData, operatorId: e.target.value });
                  if (errors.operatorId) setErrors({ ...errors, operatorId: '' });
                }}
                disabled={loading || mode === 'edit'}
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  Select an operator
                </MenuItem>
                {operators.map((op) => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.name} ({op.username})
                  </MenuItem>
                ))}
              </Select>
              {errors.operatorId && (
                <Typography sx={{ color: '#F44336', fontSize: '12px', mt: 0.5 }}>
                  {errors.operatorId}
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* Game Selection */}
          <Box>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
              Game *
            </Typography>
            <FormControl fullWidth size="small" error={!!errors.gameSlug}>
              <Select
                value={formData.gameSlug}
                onChange={(e) => handleGameChange(e.target.value)}
                disabled={loading || gamesLoading || mode === 'edit'}
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  {gamesLoading ? 'Loading games...' : 'Select a game'}
                </MenuItem>
                {games.map((game) => (
                  <MenuItem key={game.id} value={game.slug}>
                    {game.name} ({game.slug})
                  </MenuItem>
                ))}
              </Select>
              {errors.gameSlug && (
                <Typography sx={{ color: '#F44336', fontSize: '12px', mt: 0.5 }}>
                  {errors.gameSlug}
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* Game Mode Selection (linked to RgsGameMode) */}
          <Box>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
              Game Mode (RgsGameMode) *
            </Typography>
            {rtpLevelsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={20} sx={{ color: '#FFC107' }} />
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
                  Loading available game modes...
                </Typography>
              </Box>
            ) : !formData.gameSlug ? (
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '13px', py: 1 }}>
                Select a game first to see available game modes
              </Typography>
            ) : allGameModes.length === 0 ? (
              <Alert severity="warning" sx={{ backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
                No game modes uploaded for this game. Upload game math first.
              </Alert>
            ) : (
              <FormControl fullWidth size="small" error={!!errors.gameModeId}>
                <Select
                  value={formData.gameModeId}
                  onChange={(e) => {
                    setFormData({ ...formData, gameModeId: e.target.value });
                    if (errors.gameModeId) setErrors({ ...errors, gameModeId: '' });
                  }}
                  disabled={loading}
                  displayEmpty
                  sx={selectSx}
                >
                  <MenuItem value="" disabled>
                    Select a game mode
                  </MenuItem>
                  {allGameModes.map((m) => (
                    <MenuItem key={m.gameModeId} value={m.gameModeId}>
                      {m.modeName} — RTP Level: {m.rtpLevel}% | RTP: {m.rtp} | Cost: {m.cost} |
                      Outcomes: {m.outcomeCount}
                    </MenuItem>
                  ))}
                </Select>
                {errors.gameModeId && (
                  <Typography sx={{ color: '#F44336', fontSize: '12px', mt: 0.5 }}>
                    {errors.gameModeId}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Selected mode details */}
            {selectedMode && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}
              >
                <Typography sx={{ color: '#4CAF50', fontSize: '13px', fontWeight: 500, mb: 0.5 }}>
                  Selected Game Mode Details:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                    <strong>Mode:</strong> {selectedMode.modeName}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                    <strong>RTP Level:</strong> {selectedMode.rtpLevel}%
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                    <strong>RTP:</strong> {selectedMode.rtp}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                    <strong>Cost:</strong> {selectedMode.cost}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                    <strong>Outcomes:</strong> {selectedMode.outcomeCount}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontSize: '11px',
                    mt: 0.5,
                    fontFamily: 'monospace'
                  }}
                >
                  ID: {selectedMode.gameModeId}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              fullWidth
              disabled={loading}
              sx={{
                flex: 1,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#fff',
                borderRadius: '4px',
                py: 1,
                fontSize: '14px',
                textTransform: 'capitalize',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                flex: 1,
                backgroundColor: '#FFC107',
                color: '#000',
                borderRadius: '4px',
                py: 1,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'capitalize',
                '&:hover': { backgroundColor: '#FFD54F' }
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#000' }} />
              ) : mode === 'create' ? (
                'Assign'
              ) : (
                'Update'
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SetOperatorRtpDialog;
